/*
 * ICT — custom.js
 *
 * Layer JS di ICT Sviluppo per arancebio-theme (Local 4.1.2 di KrownThemes).
 * Regola del repo (CLAUDE.md §6, regola 2): il JS globale ICT vive qui, caricato con `defer`
 * dopo gli asset del vendor. Il namespace `KROWN` del vendor si legge, non si estende:
 * il codice ICT sta sotto `window.ICT`.
 *
 * Convenzioni: una regione per componente, delimitata da un commento con il riferimento
 * alla richiesta/issue/PR. Nessun accesso a cookie o localStorage senza passare dal
 * consent manager (Complianz, CLAUDE.md §7).
 */

window.ICT = window.ICT || {};

/* ==========================================================================
   Card prodotto in PLP con swatch dell'app Variant King (StarApps) — branch fix-vari-140926
   ========================================================================== */
/*
 * Gli swatch nelle card della collezione sono iniettati dall'app Variant King (app embed
 * `sa-variants`, combined listings). Al click (o hover) su uno swatch l'app:
 *   1. scrive il nuovo titolo con `innerHTML` direttamente in `.product-item__title`,
 *      cancellando il wrapper del tema (`.remove-line-height-space--small > span.h5 / .text-size--*`):
 *      il titolo perde la sua dimensione e non la recupera più;
 *   2. sostituisce il contenuto di `.product-price` con il proprio template, che conosce solo
 *      prezzo e prezzo barrato: il prezzo unitario (`.product-price--unit`, "€10,97 EUR/kg")
 *      sparisce. Il motore di template dell'app non ha un placeholder per il prezzo unitario,
 *      quindi non è risolvibile dalla configurazione dell'app.
 *
 * Qui si ascolta l'evento `vkcl:product:changed` che l'app emette sulla card PRIMA di
 * riscrivere il DOM, si fotografa il wrapper del titolo e, al tick successivo (l'app aggiorna
 * in modo sincrono), si ripristina il wrapper con il nuovo titolo e si riaggiunge il prezzo
 * unitario letto da `/products/<handle>.js`.
 *
 * Configurazione dal tag <script> in layout/theme.liquid (data attributes):
 *   data-money-with-currency-format  → shop.money_with_currency_format (es. "€{{amount_with_comma_separator}} EUR")
 *   data-unit-price-label            → 'general.accessibility_labels.price.unit' | t
 */
(function () {
  'use strict';

  var script = document.currentScript;
  var config = {
    unitFormat: (script && script.dataset.moneyWithCurrencyFormat) || '{{amount}}',
    unitLabel: (script && script.dataset.unitPriceLabel) || ''
  };

  var SEL = {
    card: '[data-js-product-item]',
    title: '.product-item__title',
    titleText: '.text-animation--underline',
    price: '.product-price',
    priceOriginal: '.product-price--original',
    unit: '.product-price--unit',
    formInput: '[name="id"]'
  };

  /* Formattazione prezzo (centesimi → stringa) sul modello del formatMoney del vendor
     in component-shipping-notice.js, che è privato e non riutilizzabile. */
  function formatMoney(cents, format) {
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var placeholder = match ? match[1] : 'amount';
    var number = Number(cents);
    if (isNaN(number)) return '';

    function withDelimiters(precision, thousands, decimal) {
      var parts = (number / 100).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    var value;
    switch (placeholder) {
      case 'amount_no_decimals': value = withDelimiters(0, ','); break;
      case 'amount_with_comma_separator': value = withDelimiters(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(0, '.'); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(0, ' '); break;
      case 'amount_with_period_and_space_separator': value = withDelimiters(2, ' ', '.'); break;
      default: value = withDelimiters(2, ',', '.');
    }
    return match ? format.replace(match[0], value) : value;
  }

  /* Stessa resa di `variant.unit_price | money_with_currency | unit_price_with_measurement`. */
  function unitPriceHtml(variant) {
    var m = variant.unit_price_measurement;
    if (!m || variant.unit_price == null) return '';
    var reference = (m.reference_value && m.reference_value !== 1 ? m.reference_value : '') + (m.reference_unit || '');
    return '<span class="visually-hidden">' + config.unitLabel + ':</span>' +
      formatMoney(variant.unit_price, config.unitFormat) + '/' + reference;
  }

  /* Fotografa il wrapper del titolo finché è ancora quello del tema. */
  function snapshotTitle(card) {
    var title = card.querySelector(SEL.title);
    if (title && title.querySelector(SEL.titleText)) {
      card.ictTitleTemplate = title.innerHTML;
    }
  }

  /* Rimette il wrapper del tema attorno al testo scritto dall'app. */
  function restoreTitle(card) {
    var title = card.querySelector(SEL.title);
    if (!title || !card.ictTitleTemplate || title.querySelector(SEL.titleText)) return;
    var newTitle = title.textContent.trim();
    title.innerHTML = card.ictTitleTemplate;
    title.querySelector(SEL.titleText).textContent = newTitle;
    title.setAttribute('title', newTitle);
  }

  function productHandle(card) {
    var link = card.querySelector(SEL.title) || card.querySelector('a[href*="/products/"]');
    var href = link && link.getAttribute('href');
    var match = href && href.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function digits(text) {
    return (text || '').replace(/\D/g, '');
  }

  /* Variante mostrata nella card: input del quick buy, altrimenti quella con lo stesso
     prezzo esposto, altrimenti la prima disponibile. */
  function displayedVariant(card, product) {
    var variants = product.variants || [];
    var input = card.querySelector(SEL.formInput);
    var byId = input && variants.filter(function (v) { return String(v.id) === String(input.value); })[0];
    if (byId) return byId;
    var priceText = digits((card.querySelector(SEL.priceOriginal) || {}).textContent);
    var byPrice = priceText && variants.filter(function (v) { return digits(String(v.price)) === priceText; })[0];
    if (byPrice) return byPrice;
    return variants.filter(function (v) { return v.available; })[0] || variants[0];
  }

  function restoreUnitPrice(card) {
    var price = card.querySelector(SEL.price);
    var handle = productHandle(card);
    if (!price || !handle) return;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var sequence = card.ictUnitSequence = (card.ictUnitSequence || 0) + 1;

    fetch(root + 'products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (product) {
        /* Nel frattempo l'utente ha cambiato ancora swatch: lascia fare alla richiesta più recente. */
        if (!product || card.ictUnitSequence !== sequence) return;
        var variant = displayedVariant(card, product);
        var html = variant ? unitPriceHtml(variant) : '';
        var current = card.querySelector(SEL.price);
        if (!current) return;
        current.querySelectorAll(SEL.unit).forEach(function (el) { el.remove(); });
        if (!html) return;
        var unit = document.createElement('span');
        unit.className = 'product-price--unit text-size--regular';
        unit.innerHTML = html;
        current.appendChild(unit);
      })
      .catch(function () { /* senza dati il prezzo unitario resta assente, come farebbe l'app */ });
  }

  document.addEventListener('vkcl:product:changed', function (event) {
    var card = event.target.closest ? event.target.closest(SEL.card) : null;
    if (!card) return;
    snapshotTitle(card);
    setTimeout(function () {
      restoreTitle(card);
      restoreUnitPrice(card);
    }, 0);
  });

  /* Fotografia iniziale, così il wrapper è noto anche se il primo evento arrivasse a DOM già riscritto. */
  function snapshotAll() {
    document.querySelectorAll(SEL.card).forEach(snapshotTitle);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', snapshotAll);
  } else {
    snapshotAll();
  }

  window.ICT.variantKingCardFix = { restoreTitle: restoreTitle, restoreUnitPrice: restoreUnitPrice };
})();
